import { reimbursementCategoryNameMessageKeys } from "../../reimbursementCategoryNameMessageKeys"
import { FormattedMessage, useIntl } from "react-intl"
import { getPiiProps } from "../../../../../services/tracking"
import { Body } from "../../../atoms/Bodies"
import { Spacer } from "../../../atoms/Atoms"
import { TextLink } from "../../../atoms/links/TextLink"
import { DbgPaths } from "../../../../../utils/Paths"
import { reimbursementCategoryNameMessages } from "../../reimbursementCategoryMessages"
import React from "react"
import { FlexContainer, H3, Icon } from "@carrotfertility/carotene"
import { AccordionWithoutMarkdown } from "../../../Accordion"
import {
  CategoryAccordionColumn,
  CategoryDivider,
  CategoryHeader,
  CategoryIconColumn,
  CategoryTitle,
  Legend,
  ReimbursementsListSection,
  ReimbursementSummary
} from "../SharedReimbursementElements"
import {
  getCategoryPendingReimbursementsList,
  getCompletedReimbursementsList,
  getReceivedReimbursementsList,
  getReimbursementsRequiringActionList
} from "../ReimbursementListUtils"
import { RetinaImage } from "../../../atoms/images/RetinaImage"
import { PlanInfoBadgeContainer } from "../../../../containers/planinfo/PlanInfoBadgeContainer"
import { CircleImage } from "../../../atoms/images/CircleImage"
import { ReimbursementDescriptionGroup, ReimbursementDescriptionList } from "../ReimbursementDescriptionList"
import Badge from "../Badge"

const WaitingReimbursementList = ({ reimbursements }) => {
  const reimbursementsRequiringAction = getReimbursementsRequiringActionList(reimbursements)
  const receivedReimbursements = getReceivedReimbursementsList(reimbursements)
  const intl = useIntl()
  return (
    <CategoryHeader>
      <CategoryIconColumn>
        <CircleImage iconName={"icn-expenses-pending"} size={36} padding={6} backgroundColor={"mint"} />
        {reimbursementsRequiringAction.length > 0 && (
          <FlexContainer
            width="10px"
            justifyContent="center"
            alignItems="center"
            sx={{ marginTop: "-36px", marginLeft: "27px" }}
          >
            <Icon name="dot" color="errorRed" size="tiny" />
          </FlexContainer>
        )}
      </CategoryIconColumn>
      <CategoryAccordionColumn>
        <AccordionWithoutMarkdown
          title={
            <>
              <H3 stack="tiny">
                <FormattedMessage defaultMessage="Waiting to be processed" />
              </H3>
              <ReimbursementDescriptionList columns={[1, null, 3]}>
                <ReimbursementDescriptionGroup
                  title={intl.formatMessage({ defaultMessage: "# of expenses" })}
                  value={reimbursementsRequiringAction.length + receivedReimbursements.length}
                />
                <ReimbursementDescriptionGroup
                  title={
                    <span style={{ whiteSpace: "nowrap" }}>
                      <FormattedMessage defaultMessage="Need your attention" />
                      {reimbursementsRequiringAction.length > 0 && (
                        <Icon sx={{ position: "relative", top: "-8px" }} name="dot" color="errorRed" size="5px" />
                      )}
                    </span>
                  }
                  value={reimbursementsRequiringAction.length}
                />
              </ReimbursementDescriptionList>
            </>
          }
          key="Waiting to be processed"
          shouldExpand={reimbursementsRequiringAction.length > 0}
          chevronOnRight={true}
          chevronHeight={17}
          disabled={reimbursementsRequiringAction.length + receivedReimbursements.length === 0}
        >
          <Spacer height={2} />
          <Legend />
          {reimbursementsRequiringAction.length > 0 && (
            <ReimbursementsListSection
              title={
                <Badge badgeContent={reimbursementsRequiringAction.length} color={"error"}>
                  <FormattedMessage defaultMessage="Needs Attention" />
                </Badge>
              }
              emptyText={intl.formatMessage({ defaultMessage: "No transactions need attention" })}
              items={reimbursementsRequiringAction}
            />
          )}
          {receivedReimbursements.length > 0 && (
            <ReimbursementsListSection
              title={intl.formatMessage({ defaultMessage: "Received" })}
              emptyText={intl.formatMessage({ defaultMessage: "No received transactions" })}
              items={receivedReimbursements}
            />
          )}
        </AccordionWithoutMarkdown>
      </CategoryAccordionColumn>
    </CategoryHeader>
  )
}

const CategoryReimbursementList = ({ reimbursements, subsidy, categorySupportsDeductibles }) => {
  const reimbursementsPending = getCategoryPendingReimbursementsList(reimbursements)
  const reimbursementsCompleted = getCompletedReimbursementsList(reimbursements)
  const intl = useIntl()
  const categoryName = intl.formatMessage(
    reimbursementCategoryNameMessages[subsidy.reimbursementCategoryNameMessageKey]
  )
  return (
    <>
      <CategoryDivider />
      <Spacer height={1} />
      <CategoryHeader>
        <CategoryIconColumn>
          <RetinaImage name={subsidy.reimbursementCategoryIconName || "icn-expenses-other"} height={36} width={36} />
        </CategoryIconColumn>
        <CategoryAccordionColumn>
          <AccordionWithoutMarkdown
            title={
              <>
                <CategoryTitle>
                  <H3>
                    <FormattedMessage
                      {...reimbursementCategoryNameMessages[subsidy.reimbursementCategoryNameMessageKey]}
                    />
                  </H3>
                  <div
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    <PlanInfoBadgeContainer
                      backgroundColor={"black-10"}
                      fontColor={"black-80"}
                      subsidy={subsidy}
                      categorySupportsDeductibles={categorySupportsDeductibles}
                    />
                  </div>
                </CategoryTitle>
                <Spacer height={1} />
                <ReimbursementSummary
                  subsidy={subsidy}
                  reimbursementsCount={reimbursementsPending.length + reimbursementsCompleted.length}
                />
              </>
            }
            key={categoryName}
            chevronOnRight={true}
            chevronHeight={17}
            disabled={reimbursementsPending.length + reimbursementsCompleted.length === 0}
          >
            <Spacer height={2} />
            <Legend />
            {reimbursementsPending.length > 0 && (
              <ReimbursementsListSection
                title={intl.formatMessage({ defaultMessage: "Pending" })}
                items={reimbursementsPending}
              />
            )}
            {reimbursementsCompleted.length > 0 && (
              <ReimbursementsListSection
                title={intl.formatMessage({ defaultMessage: "Completed" })}
                items={reimbursementsCompleted}
              />
            )}
          </AccordionWithoutMarkdown>
        </CategoryAccordionColumn>
      </CategoryHeader>
    </>
  )
}
export const MultipleCategoriesReimbursementsLists = ({ reimbursementsList, subsidies }) => {
  const hasQmeCategory = subsidies?.some(
    (s) => s.reimbursementCategoryNameMessageKey === reimbursementCategoryNameMessageKeys.QME
  )
  const intl = useIntl()
  return (
    <div {...getPiiProps()}>
      <Body color={"black-60"} weight={350}>
        <FormattedMessage defaultMessage="Your employer offers separate fund categories for different kinds of eligible care and services. Funds from one category cannot be transferred to another." />
        <Spacer height={1} />
        <FormattedMessage
          defaultMessage="Check your <link>benefit guide</link> to learn what's covered."
          values={{
            link: (linkContent) => (
              <TextLink
                to={DbgPaths.PAYING_FOR_CARE}
                className={"bb b--light-carrot-orange no-underline pointer cc dim animate-all lh-copy"}
              >
                {linkContent}
              </TextLink>
            )
          }}
        />
      </Body>
      <Spacer height={1.5} />
      <WaitingReimbursementList reimbursements={reimbursementsList} />
      {subsidies.map((subsidy) => {
        const reimbursements = reimbursementsList.filter(
          (r) => r.expensePlanCategoryNameMessageKey === subsidy.reimbursementCategoryNameMessageKey
        )
        const categoryNameMessageKey = subsidy.reimbursementCategoryNameMessageKey
        const categoryName = intl.formatMessage(reimbursementCategoryNameMessages[categoryNameMessageKey])
        const categorySupportsDeductibles =
          categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION &&
          categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.GC &&
          categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC &&
          (categoryNameMessageKey !== reimbursementCategoryNameMessageKeys.QME || !hasQmeCategory)
        return (
          <CategoryReimbursementList
            reimbursements={reimbursements}
            subsidy={subsidy}
            categorySupportsDeductibles={categorySupportsDeductibles}
            key={categoryName}
          />
        )
      })}
    </div>
  )
}
